import { useState } from 'react';
import SideSheet from '..';
import { formatToNaira, copyToClipboard } from '../../../helpers/utils';
import './GetItPurchaseDetails.scss';
import useGetIt from '../../../hooks/useGetIt';
import useCustomers from '../../../hooks/useCustomers';
import { Loader } from '@piggytechng/component-library';
import { IoCopyOutline } from 'react-icons/io5';
import dayjs from 'dayjs';

import { GetItPurchaseDetailsProps } from '../../../types';

const GetItPurchaseDetails = ({ show, onClose, data, subscription, subId }: GetItPurchaseDetailsProps) => {
  const [view, setView] = useState<'details' | 'delivery'>('details');
  const { singleCustomer } = useCustomers();
  const { deliveryStatus, fetchingDelivery } = useGetIt({
    subId: subId,
    enabled: show,
  });

  if (!data) return null;

  const isDelivered = deliveryStatus?.is_delivered;
  // const completionPercentage = isDelivered ? 100 : 67;

  const handleClose = () => {
    setView('details');
    onClose();
  };

  return (
    <SideSheet
      active={show}
      onClose={handleClose}
      title={view === 'details' ? 'Purchase Details' : 'Delivery Progress'}
      subTitle={
        view === 'details' ? (
          <div className="flex-column" style={{ gap: '8px', display: 'flex', flexDirection: 'column' }}>
            <div className="flex-row items-center" style={{ gap: '8px', display: 'flex', alignItems: 'center' }}>
              <span>ID: {data.transaction_id || subscription?.reference}</span>
              <IoCopyOutline
                className="pointer"
                size={16}
                onClick={() => copyToClipboard('Reference', data.transaction_id || subscription?.reference || '')}
                title="Copy ID"
              />
            </div>
          </div>
        ) : (
          `Product: ${subscription?.purchase.item.title || 'Product'}`
        )
      }
      canGoBack={view === 'delivery'}
      goBack={() => setView('details')}
    >
      <div className="getit-details-sheet">
        {view === 'details' ? (
          <>
            <section className="status-section">
              <h4>Status</h4>
              <span className="status-badge active">{subscription?.status || 'Active'}</span>
            </section>

            <section className="info-grid">
              <div className="grid-item full">
                <h4>User Information</h4>
                <div className="flex-row">
                  <div className="sub-item">
                    <label>Name</label>
                    <p>
                      {singleCustomer ? `${singleCustomer.firstName} ${singleCustomer.lastName}` : data.name || 'N/A'}
                    </p>
                  </div>
                  <div className="sub-item">
                    <label>Email</label>
                    <p>{singleCustomer?.email || subscription?.reference || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="grid-item full">
                <h4>Product Information</h4>
                <div className="flex-row">
                  <div className="sub-item">
                    <label>Product</label>
                    <p>{subscription?.purchase.item.title || 'Product'}</p>
                  </div>
                  <div className="sub-item">
                    <label>Targeted Amount</label>
                    <p>{formatToNaira(subscription?.amounts.target_amount || 0)}</p>
                  </div>
                </div>
                <div className="flex-row mt-4">
                  <div className="sub-item">
                    <label>Amount Paid</label>
                    <p>{formatToNaira(subscription?.amounts.total_saved || 0)}</p>
                  </div>
                  <div className="sub-item">
                    <label>Balance</label>
                    <p>{formatToNaira(subscription?.amounts.remaining_amount || 0)}</p>
                  </div>
                </div>
                {data.external_reference && (
                  <div className="flex-row mt-4">
                    <div className="sub-item">
                      <label>External Reference</label>
                      <p>{data.external_reference}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid-item full">
                <h4>Payment Progress</h4>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${subscription?.amounts.progress || 0}%` }} />
                  </div>
                  <p className="progress-label">{subscription?.amounts.progress || 0}% Complete</p>
                </div>
              </div>

              <div className="grid-item full">
                <h4>Payment Schedule</h4>
                <div className="flex-row">
                  <div className="sub-item">
                    <label>Duration</label>
                    <p>{subscription?.duration.days_remaining} days left</p>
                  </div>
                  <div className="sub-item">
                    <label>Frequency</label>
                    <p>{subscription?.payment_schedule.cadence_label || 'monthly'}</p>
                  </div>
                </div>
                <div className="flex-row mt-4">
                  <div className="sub-item">
                    <label>Start Date</label>
                    <p>{dayjs(subscription?.duration.created_at).format('MMM D, YYYY')}</p>
                  </div>
                  <div className="sub-item">
                    <label>End Date</label>
                    <p>{dayjs(subscription?.duration.target_date).format('MMM D, YYYY')}</p>
                  </div>
                </div>
                {!isDelivered && (
                  <div className="flex-row mt-4">
                    <div className="sub-item">
                      <label>Next Payment</label>
                      <p>{dayjs(subscription?.payment_schedule.next_debit_at).format('MMM D, YYYY')}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid-item full">
                <h4>Delivery Details</h4>
                {deliveryStatus?.destination ? (
                  <div className="sub-item">
                    <label>Address</label>
                    <p>{`${deliveryStatus.destination.address}, ${deliveryStatus.destination.city}, ${deliveryStatus.destination.state}`}</p>
                  </div>
                ) : (
                  <p>Address information hidden until delivery begins.</p>
                )}
              </div>

              <div className="grid-item full">
                <h4>Delivery</h4>
                <button className="delivery-btn" onClick={() => setView('delivery')}>
                  View Delivery Progress
                </button>
              </div>
            </section>
          </>
        ) : (
          <div className="delivery-progress-view">
            <section className="status-section">
              <h4>Status</h4>
              <span className={`status-badge ${isDelivered ? 'active' : 'pending'}`}>
                {isDelivered ? 'Delivered' : 'Pending'}
              </span>
            </section>

            <section className="delivery-timeline">
              <h4>Delivery Timeline</h4>

              <div className="timeline-container">
                {fetchingDelivery && !deliveryStatus ? (
                  <div className="flex--2" style={{ padding: '20px', justifyContent: 'center' }}>
                    <Loader color={'#F37932'} />
                  </div>
                ) : deliveryStatus && deliveryStatus.has_delivery ? (
                  deliveryStatus.history.length > 0 ? (
                    deliveryStatus.history.map((step, idx) => (
                      <div key={idx} className={`timeline-step ${idx === 0 ? 'active' : ''}`}>
                        <div className="step-marker">
                          <div className="circle">{idx + 1}</div>
                          {idx < deliveryStatus.history.length - 1 && <div className="line" />}
                        </div>
                        <div className="step-card">
                          <h5>{step.status}</h5>
                          <p className="desc">{step.description}</p>
                          <p className="date">{dayjs(step.timestamp).format('MMM D, YYYY | HH:mm')}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-history">No delivery history available.</div>
                  )
                ) : (
                  <div className="no-delivery">No delivery information available.</div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </SideSheet>
  );
};

export default GetItPurchaseDetails;
